import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import {
  COMMISSION_MODELS,
  LEAD_DELIVERY_METHODS,
  PARTNER_CATEGORIES,
  PARTNER_STATUSES,
  getCategoryLabel,
} from "../../../constants/automapPartnershipConstants";
import { createPartner, fetchPartner, updatePartner } from "../../../services/automapPartnershipApi";
import { AdminPartnersNav } from "./AdminPartnersShared";

const EMPTY = {
  name: "",
  slug: "",
  category: "financing",
  status: "draft",
  description: "",
  publicDescription: "",
  contactName: "",
  contactEmail: "",
  contactWhatsapp: "",
  websiteUrl: "",
  citiesServed: "",
  departmentsServed: "",
  serviceAreas: "",
  routingPriority: 10,
  leadDeliveryMethod: "admin_manual",
  leadDeliveryEmail: "",
  leadDeliveryWhatsapp: "",
  commissionModel: "unknown",
  commissionNotes: "",
  complianceNotes: "",
  disclosureText: "",
};

function splitList(value) {
  return String(value || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function AdminPartnerFormPage() {
  const { partnerId } = useParams();
  const isEdit = Boolean(partnerId);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    fetchPartner(partnerId)
      .then((data) => {
        const p = data.partner;
        setForm({
          ...p,
          citiesServed: (p.citiesServed || []).join(", "),
          departmentsServed: (p.departmentsServed || []).join(", "),
          serviceAreas: (p.serviceAreas || []).join(", "),
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [isEdit, partnerId]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const body = {
      ...form,
      citiesServed: splitList(form.citiesServed),
      departmentsServed: splitList(form.departmentsServed),
      serviceAreas: splitList(form.serviceAreas),
      routingPriority: Number(form.routingPriority) || 10,
    };
    try {
      if (isEdit) {
        await updatePartner(partnerId, body);
        navigate(`/admin/partners/${partnerId}`);
      } else {
        const res = await createPartner(body);
        navigate(`/admin/partners/${res.partner.id}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPageLayout>
      <AdminPageHeader eyebrow="AutoMapa · Aliados" title={isEdit ? "Editar aliado" : "Nuevo aliado"} />
      <AdminAutomotiveNav active="partners" />
      <AdminPartnersNav />
      <main className="admin-console__main wrap">
        {loading ? <p>Cargando…</p> : null}
        {error ? <p className="dealer-state-box">{error}</p> : null}
        {!loading ? (
          <form className="dealer-state-box" onSubmit={handleSubmit} style={{ display: "grid", gap: "0.75rem", maxWidth: 640 }}>
            <label>Nombre<input required value={form.name} onChange={(e) => set("name", e.target.value)} /></label>
            <label>Slug<input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto-generado si vacío" /></label>
            <label>Categoría
              <select value={form.category} onChange={(e) => set("category", e.target.value)}>
                {PARTNER_CATEGORIES.map((c) => <option key={c} value={c}>{getCategoryLabel(c)}</option>)}
              </select>
            </label>
            <label>Estado
              <select value={form.status} onChange={(e) => set("status", e.target.value)}>
                {PARTNER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label>Descripción pública<textarea rows={2} value={form.publicDescription} onChange={(e) => set("publicDescription", e.target.value)} /></label>
            <label>Contacto<input value={form.contactName} onChange={(e) => set("contactName", e.target.value)} /></label>
            <label>Email<input value={form.contactEmail} onChange={(e) => set("contactEmail", e.target.value)} /></label>
            <label>WhatsApp<input value={form.contactWhatsapp} onChange={(e) => set("contactWhatsapp", e.target.value)} /></label>
            <label>Ciudades (coma)<input value={form.citiesServed} onChange={(e) => set("citiesServed", e.target.value)} /></label>
            <label>Áreas servicio (coma)<input value={form.serviceAreas} onChange={(e) => set("serviceAreas", e.target.value)} /></label>
            <label>Prioridad routing<input type="number" value={form.routingPriority} onChange={(e) => set("routingPriority", e.target.value)} /></label>
            <label>Entrega leads
              <select value={form.leadDeliveryMethod} onChange={(e) => set("leadDeliveryMethod", e.target.value)}>
                {LEAD_DELIVERY_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label>Email entrega<input value={form.leadDeliveryEmail} onChange={(e) => set("leadDeliveryEmail", e.target.value)} /></label>
            <label>Comisión
              <select value={form.commissionModel} onChange={(e) => set("commissionModel", e.target.value)}>
                {COMMISSION_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label>Notas compliance<textarea rows={2} value={form.complianceNotes} onChange={(e) => set("complianceNotes", e.target.value)} /></label>
            <label>Texto divulgación (opcional)<textarea rows={2} value={form.disclosureText || ""} onChange={(e) => set("disclosureText", e.target.value)} /></label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button type="submit" className="dealer-portal-btn" disabled={saving}>{saving ? "Guardando…" : "Guardar"}</button>
              <Link to={isEdit ? `/admin/partners/${partnerId}` : "/admin/partners"}>Cancelar</Link>
            </div>
          </form>
        ) : null}
      </main>
    </AdminPageLayout>
  );
}
